import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { search, getExploreIdleUsers } from "../../api/api";
import { parseExplorePlace } from "../../lib/explorePlace";
import { isAuthHeroPath } from "../../lib/authHero";
import { cn } from "../../lib/utils";
import FlatMap from "./map/FlatMap";

/**
 * Parse a temporary map focus pin from explore URL params (`lat`, `lon`/`lng`, optional `label`).
 * @param {URLSearchParams} searchParams
 * @returns {{ latitude: number, longitude: number, name: string } | null}
 */
function parseFocusLocation(searchParams) {
  const lat = Number(searchParams.get("lat"));
  const lon = Number(searchParams.get("lon") || searchParams.get("lng"));
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || (lat === 0 && lon === 0)) {
    return null;
  }
  const label = (searchParams.get("label") || "").trim();
  return {
    latitude: lat,
    longitude: lon,
    name: label || "Post location",
  };
}

/**
 * Persistent Mapbox instance for the app shell.
 * Always mounted so route / auth chrome swaps do not rebuild WebGL.
 * Hidden on non-map routes; autorotation pauses while hidden.
 *
 * Hero surfaces (`/`, `/login`): read-only idle globe + spin.
 * Explore: interactive map.
 *
 * @param {Object} props
 * @param {number|string} props.userId
 * @param {Object|null} [props.user]
 * @param {number} props.topOffsetPx Distance from viewport top to the map (below header).
 * @returns {JSX.Element}
 */
function SharedMapLayer({ userId, user, topOffsetPx }) {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isHome = pathname === "/";
  const isLogin = isAuthHeroPath(pathname);
  const isExplore = pathname === "/explore";
  /** Landing-style read-only globe (home + login). */
  const isHeroSurface = isHome || isLogin;
  const isMapRoute = isHeroSurface || isExplore;
  const queryData = searchParams.get("q") || "";
  const selectedPlace = useMemo(
    () => (isExplore ? parseExplorePlace(searchParams) : null),
    [isExplore, searchParams]
  );
  const focusLocation = useMemo(
    () => (isExplore && !selectedPlace ? parseFocusLocation(searchParams) : null),
    [isExplore, searchParams, selectedPlace]
  );

  const [idleUsers, setIdleUsers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);

  // Prefetch idle pins once the shell map exists (our API, not Mapbox).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getExploreIdleUsers(userId ?? 0, 15);
      if (!cancelled && res && res.success && Array.isArray(res.data)) {
        setIdleUsers(res.data);
      } else if (!cancelled) {
        setIdleUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Search hits on explore with a query and/or a committed city (post-location pins win).
  useEffect(() => {
    if (!isExplore || focusLocation) {
      setSearchResults([]);
      return undefined;
    }
    if (!queryData && !selectedPlace) {
      setSearchResults([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const res = await search(
        userId,
        queryData,
        false,
        false,
        true,
        true,
        undefined,
        null,
        null,
        null,
        selectedPlace?.bbox || null
      );
      if (cancelled) return;
      if (res && res.success && Array.isArray(res.data)) {
        setSearchResults(res.data);
      } else {
        setSearchResults([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isExplore, queryData, userId, focusLocation, selectedPlace]);

  const idleMapPins = useMemo(() => {
    if (!idleUsers.length) return [];
    return idleUsers.filter(
      (item) => item?.type === "user" || item?.type === "organization"
    );
  }, [idleUsers]);

  const focusPin = useMemo(() => {
    if (!focusLocation) return null;
    return {
      type: "location",
      id: "post-location-focus",
      name: focusLocation.name,
      latitude: focusLocation.latitude,
      longitude: focusLocation.longitude,
    };
  }, [focusLocation]);

  const mapResults = focusPin
    ? [focusPin]
    : isExplore && (queryData || selectedPlace)
      ? searchResults
      : idleMapPins;

  const hasFocusedResults = Boolean(focusPin)
    || Boolean(isExplore && (queryData || selectedPlace));

  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 min-h-0 w-full",
        // Explore: above page content so drag/zoom work; otherwise under content.
        isExplore ? "z-20" : "z-0",
        // Keep layout size for Mapbox; hide without display:none.
        !isMapRoute && "invisible pointer-events-none",
        (isHeroSurface || !isMapRoute) && "pointer-events-none"
      )}
      style={{ top: topOffsetPx }}
      aria-hidden={!isExplore || undefined}
    >
      <FlatMap
        userId={userId}
        user={user}
        results={mapResults}
        cameraBounds={selectedPlace?.bbox || null}
        idleSpotlightPins={isHeroSurface || !hasFocusedResults}
        readOnly={isHeroSurface || !isMapRoute}
        embedVariant={isHeroSurface ? "hero" : "default"}
        cameraPaddingLeft={isExplore ? 36 : 0}
        active={isMapRoute}
        topOffsetPx={topOffsetPx}
      />
    </div>
  );
}

export default SharedMapLayer;
